<template>
   <div class="file-upload-component">
      <div
         class="upload-box rounded p-4 text-center mb-3"
         @dragover.prevent
         @drop.prevent="handleDrop"
      >
         <div class="mb-3">
            <img
               src="@/assets/img/upload-file.png"
               style="height: 25px; width: 30px"
               alt="upload"
            />
            <div class="font-weight-bold file-upload-label">Drag and drop files here</div>
            <div class="file-upload-label text-muted mb-1">or</div>
            <button
               :disabled="disabled"
               class="btn btn-primary light-button file-upload-label btn-color"
               @click="triggerFileInput"
            >
               Select Files
            </button>
            <input
               type="file"
               :multiple="multiple"
               ref="fileInput"
               class="d-none"
               :disabled="disabled"
               @change="handleFileSelect"
               :accept="acceptFileTypes"
            />
         </div>
         <small class="text-muted mt-4 file-upload-label">{{ limits }}</small>
      </div>

      <div class="file-preview-container" v-if="files.length > 0">
         <FileUploadPreviewItem
            v-for="(file, index) in files"
            :key="fileKey(file, index)"
            :file="file"
            :ident="`upload-document-preview-${index}`"
            :disabled="disabled"
            :canDeleteFiles="canDeleteFiles"
            @delete="removeFile(index)"
            @change="updateFile(index, $event)"
         />
      </div>
      <div v-else class="text-muted file-upload-label">No files selected yet.</div>
   </div>
</template>

<script>
import FileUploadPreviewItem from '@/components/FileUploadPreviewItem.vue';

export default {
   name: 'FileUploadSelectPeriodComponent',
   components: {FileUploadPreviewItem},
   props: {
      currentFiles: {
         type: Array,
         default: () => [],
      },
      uploadCategoryId: {
         type: [String, Number],
         default: null,
      },
      disabled: {
         type: Boolean,
         default: false,
      },
      multiple: {
         type: Boolean,
         default: false,
      },
      limits: {
         type: String,
         default: 'XLSX or CSV | Limit 10MB',
      },
      canDeleteFiles: {
         type: Boolean,
         default: true,
      },
      acceptFileTypes: {
         type: String,
         default: '*.*',
      },
   },
   data() {
      return {
         files: [],
         uploading: false,
      };
   },
   watch: {
      currentFiles: {
         immediate: true,
         handler(newVal) {
            this.files = newVal.map((file) => this.normalizeExistingFile(file));
         },
      },
   },
   methods: {
      normalizeExistingFile(file) {
         return {
            ...file,
            existing: true,
         };
      },
      normalizeNewFile(file) {
         return {
            file,
            name: file.name,
            size: file.size,
            periodId: null,
            description: '',
            existing: false,
         };
      },
      fileKey(file, index) {
         if (file.id != null) {
            return `existing-${file.id}`;
         }
         if (file.file) {
            return `new-${file.file.name}-${file.file.size}-${index}`;
         }
         return `file-${index}`;
      },
      clearFiles() {
         this.files = this.currentFiles.map((file) => this.normalizeExistingFile(file));
         if (this.$refs.fileInput) {
            this.$refs.fileInput.value = null;
         }
      },
      triggerFileInput() {
         this.$refs.fileInput.click();
      },
      handleFileSelect(event) {
         const selected = this.multiple ? Array.from(event.target.files) : [event.target.files[0]];
         this.addFiles(selected);
      },
      handleDrop(event) {
         if (this.disabled) {
            return;
         }
         const dropped = this.multiple
            ? Array.from(event.dataTransfer.files)
            : [event.dataTransfer.files[0]];
         this.addFiles(dropped);
      },
      addFiles(newFiles) {
         const validFiles = newFiles.filter(Boolean).map((file) => this.normalizeNewFile(file));
         if (validFiles.length === 0) {
            return;
         }
         this.files = [...this.files, ...validFiles];
         this.$refs.fileInput.value = null;
      },
      async updateFile(index, updatedFile) {
         this.$set(this.files, index, updatedFile);

         if (updatedFile.existing && this.uploadCategoryId !== null && updatedFile.id != null) {
            await this.$store.dispatch('uploads/updateFileDetails', {
               uploadCategoryId: this.uploadCategoryId,
               fileId: updatedFile.id,
               periodId: updatedFile.periodId,
               description: updatedFile.description,
            });
         }
      },
      async removeFile(index) {
         const fileToDelete = this.files[index];

         if (fileToDelete?.existing && fileToDelete.id != null && this.uploadCategoryId !== null) {
            await this.$store.dispatch('uploads/deleteFile', {
               fileId: fileToDelete.id,
               uploadCategoryId: this.uploadCategoryId,
               periodId: fileToDelete.periodId ?? null,
               force: true,
            });
         }

         this.files.splice(index, 1);
         if (this.$refs.fileInput) {
            this.$refs.fileInput.value = null;
         }
      },
      async finish() {
         if (this.uploading) {
            return;
         }

         const pending = this.files.filter((file) => !file.existing);
         if (pending.length === 0) {
            return;
         }

         this.uploading = true;
         try {
            const fileMap = new Map(
               this.files.filter((f) => f.existing && f.id != null).map((f) => [f.id, f])
            );
            for (const pendingFile of pending) {
               const summary = await this.$store.dispatch('uploads/uploadFiles', {
                  uploadCategoryId: this.uploadCategoryId,
                  files: [pendingFile.file],
                  periodId: pendingFile.periodId,
                  description: pendingFile.description,
               });
               summary.forEach((item) => fileMap.set(item.id, {...item, existing: true}));
            }
            this.files = Array.from(fileMap.values());
            if (this.$refs.fileInput) {
               this.$refs.fileInput.value = null;
            }
         } finally {
            this.uploading = false;
         }
      },
   },
};
</script>

<style scoped lang="scss">
.upload-box {
   border: 2px dashed #ccc;
}
.upload-box.dragover {
   background-color: #e9f5ff;
}
.file-upload-label {
   font-size: 13px;
}

.upload-box--file {
   background-color: #f9f9f9;
   margin-bottom: 10px;
}

.btn-color {
   background-color: #0978cd;
}
::v-deep .p-5 {
   padding: 1rem !important;
}
</style>
